/**
 * Wire-format mappers — snake_case JSON DTOs ↔ camelCase TS domain types.
 *
 * Per FD § 3.2: every method on `ContactsClient` translates wire-snake to
 * domain-camel at the boundary so the rest of the dashboard stays
 * idiomatic. Centralized here so adding a new field is one change.
 */

import type {
  ContactDto,
  MemoryDto,
  MemorySettingsDto,
  PaginatedDto,
  RotateSigningKeyResponseDto,
  RunSummaryDto,
  SessionSummaryDto,
  SigningKeyDto,
  SoftDeleteResponseDto,
} from "./dtos";
import type {
  Contact,
  Memory,
  MemorySettings,
  Paginated,
  RotateSigningKeyResult,
  RunSummary,
  SessionSummary,
  SigningKey,
  SoftDeleteResult,
} from "./types";

export function mapContactDto(dto: ContactDto): Contact {
  return {
    id: dto.id,
    workspaceId: dto.workspace_id,
    userId: dto.user_id,
    displayName: dto.display_name,
    email: dto.email,
    phone: dto.phone,
    avatarUrl: dto.avatar_url,
    locale: dto.locale,
    timezone: dto.timezone,
    metadata: dto.metadata,
    identityVerified: dto.identity_verified,
    source: dto.source,
    isDisabled: dto.is_disabled,
    disabledReason: dto.disabled_reason,
    deletedAt: dto.deleted_at ? new Date(dto.deleted_at) : null,
    firstSeenAt: new Date(dto.first_seen_at),
    lastSeenAt: new Date(dto.last_seen_at),
    createdAt: new Date(dto.created_at),
    updatedAt: new Date(dto.updated_at),
  };
}

export function mapSessionSummaryDto(dto: SessionSummaryDto): SessionSummary {
  return {
    id: dto.id,
    channel: dto.channel,
    startedAt: new Date(dto.started_at),
    lastActivityAt: new Date(dto.last_activity_at),
    endedAt: dto.ended_at ? new Date(dto.ended_at) : null,
  };
}

export function mapRunSummaryDto(dto: RunSummaryDto): RunSummary {
  return {
    id: dto.id,
    sessionId: dto.session_id,
    status: dto.status,
    startedAt: new Date(dto.started_at),
    endedAt: dto.ended_at ? new Date(dto.ended_at) : null,
    entryAgentId: dto.entry_agent_id,
    entryAgentVersionId: dto.entry_agent_version_id,
  };
}

export function mapSigningKeyDto(dto: SigningKeyDto): SigningKey {
  return {
    kid: dto.kid,
    algorithm: dto.algorithm,
    publicKeyPem: dto.public_key_pem,
    status: dto.status,
    rotatedAt: dto.rotated_at ? new Date(dto.rotated_at) : null,
    createdAt: new Date(dto.created_at),
  };
}

export function mapRotateSigningKeyResponse(
  dto: RotateSigningKeyResponseDto,
): RotateSigningKeyResult {
  return {
    current: mapSigningKeyDto(dto.current),
    previous: dto.previous ? mapSigningKeyDto(dto.previous) : null,
    rotatedAt: new Date(dto.rotated_at),
  };
}

export function mapSoftDeleteDto(dto: SoftDeleteResponseDto): SoftDeleteResult {
  return { id: dto.id, deletedAt: new Date(dto.deleted_at) };
}

export function mapMemoryDto(dto: MemoryDto): Memory {
  return {
    id: dto.id,
    workspaceId: dto.workspace_id,
    scope: dto.scope,
    stage: dto.stage,
    visibility: dto.visibility,
    ownerAgentId: dto.owner_agent_id,
    ownerAgentName: dto.owner_agent_name,
    contactId: dto.contact_id,
    kind: dto.kind,
    text: dto.text,
    variableId: dto.variable_id,
    variableKey: dto.variable_key,
    variableDisplayName: dto.variable_display_name,
    value: dto.value,
    salience: dto.salience,
    createdByKind: dto.created_by_kind,
    sourceRunId: dto.source_run_id,
    sourceMessageId: dto.source_message_id,
    expiresAt: dto.expires_at ? new Date(dto.expires_at) : null,
    createdAt: new Date(dto.created_at),
    updatedAt: new Date(dto.updated_at),
  };
}

export function mapMemorySettingsDto(dto: MemorySettingsDto): MemorySettings {
  return {
    memoryEnabled: dto.memory_enabled,
    memoryRecallTopK: dto.memory_recall_top_k,
    memoryRetentionDays: dto.memory_retention_days,
  };
}

export function mapPaginated<TDto, T>(
  dto: PaginatedDto<TDto>,
  mapItem: (item: TDto) => T,
): Paginated<T> {
  return {
    items: dto.items.map(mapItem),
    nextCursor: dto.next_cursor,
    hasMore: dto.has_more,
  };
}

/** Build the snake_case JSON body for a contact profile patch. */
export function profileToSnake(
  profile: Partial<{
    displayName: string | null;
    email: string | null;
    phone: string | null;
    avatarUrl: string | null;
    locale: string | null;
    timezone: string | null;
    metadata: Record<string, unknown>;
  }>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (profile.displayName !== undefined) out["display_name"] = profile.displayName;
  if (profile.email !== undefined) out["email"] = profile.email;
  if (profile.phone !== undefined) out["phone"] = profile.phone;
  if (profile.avatarUrl !== undefined) out["avatar_url"] = profile.avatarUrl;
  if (profile.locale !== undefined) out["locale"] = profile.locale;
  if (profile.timezone !== undefined) out["timezone"] = profile.timezone;
  if (profile.metadata !== undefined) out["metadata"] = profile.metadata;
  return out;
}
